import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { EnvelopeSimple, Lock, User, GoogleLogo, MicrosoftOutlookLogo, ArrowRight, Buildings, Eye, EyeSlash, Kanban, ChartBar, UsersThree } from '@phosphor-icons/react';
import BrandLogo from '../components/BrandLogo';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login, register, API } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation');
  const emailParam = searchParams.get('email');
  const [tab, setTab] = useState(invitationToken ? 'register' : 'login');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [invitationInfo, setInvitationInfo] = useState(null);
  const [pendingVerification, setPendingVerification] = useState(null); // {email}
  const [needsVerification, setNeedsVerification] = useState(null); // {email}
  const [resendStatus, setResendStatus] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);

  // Login form
  const [loginEmail, setLoginEmail] = useState(emailParam || '');
  const [loginPassword, setLoginPassword] = useState('');

  // Register form
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState(emailParam || '');
  const [regPassword, setRegPassword] = useState('');
  const [regOrgName, setRegOrgName] = useState('');

  useEffect(() => {
    if (!invitationToken) return;
    axios.get(`${API}/invitations/${invitationToken}`)
      .then(r => { setInvitationInfo(r.data); if (r.data?.email) setRegEmail(r.data.email); })
      .catch(err => setError(err.response?.data?.detail || 'Invalid invitation link'));
  }, [invitationToken, API]);

  // Surface the error=... query param set by our SSO backend on failed
  // provider callbacks so the user gets a clear message instead of a
  // silently stuck page.
  useEffect(() => {
    const code = searchParams.get('error');
    if (!code) return;
    const msg = {
      sso_not_configured: 'Google / Microsoft sign-in is not configured on this workspace yet. Please contact your admin.',
      no_code: 'Sign-in was cancelled before completing. Please try again.',
      no_email: 'Your Google / Microsoft account did not return an email address.',
      google_auth_failed: 'Google sign-in failed. Please try again or use email + password.',
      microsoft_auth_failed: 'Microsoft sign-in failed. Please try again or use email + password.',
      microsoft_auth_url_failed: 'Microsoft sign-in could not be started. Please try again.',
    }[code] || `Sign-in failed: ${code}`;
    setError(msg);
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(loginEmail, loginPassword);
      navigate('/dashboard');
    } catch (err) {
      if (err.response?.data?.needs_verification) {
        setNeedsVerification({ email: err.response.data.email || loginEmail });
      } else {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!invitationToken && !regOrgName.trim()) { setError('Organization / Company name is required'); return; }
    setSubmitting(true);
    try {
      const result = await register(regEmail, regPassword, regName, invitationToken || undefined, invitationToken ? undefined : regOrgName.trim());
      if (result?.pending_verification) {
        setPendingVerification({ email: result.email });
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendVerification = async (email) => {
    setResendStatus('sending');
    try {
      await axios.post(`${API}/auth/resend-verification`, { email }, { withCredentials: true });
      setResendStatus('sent');
      setTimeout(() => setResendStatus(''), 4000);
    } catch (err) {
      setResendStatus('error');
      setError(err.response?.data?.detail || 'Failed to resend');
    }
  };

  const handleGoogleLogin = () => {
    // Full-page redirect to our SSO initiator — the server 302s to Google,
    // then Google 302s back to /api/auth/sso/google/callback which ultimately
    // lands the user on /sso-callback (merge/login) or /sso-complete (new).
    window.location.href = `${API}/auth/sso/google`;
  };

  const handleMicrosoftLogin = () => {
    window.location.href = `${API}/auth/sso/microsoft`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left: Brand panel — sticks to viewport while form scrolls so the
          panel never gets cramped on the taller Sign Up form. */}
      <div className="hidden lg:block lg:sticky lg:top-0 lg:h-screen relative bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/25 via-slate-900 to-purple-600/20" />
        <div className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative z-10 flex flex-col h-full p-10 text-white w-full">
          <div className="flex items-center gap-2.5 mb-10" data-testid="login-brand-mark">
            <BrandLogo size="lg" invert />
            <span className="text-2xl font-bold tracking-tight">Inspiro Projects</span>
          </div>

          <div className="max-w-md flex-1 flex flex-col justify-center">
            <h1 className="font-['Outfit'] text-4xl xl:text-5xl font-semibold mb-4 leading-[1.1] tracking-tight">
              Project management<br />
              built for teams<br />
              <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">that ship faster.</span>
            </h1>
            <p className="text-base xl:text-lg text-slate-300 leading-relaxed">
              Plan work on Kanban boards, track progress in real time, and collaborate with your team — all in one beautifully simple workspace.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-8">
            <div className="p-3.5 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
              <Kanban weight="fill" className="w-5 h-5 text-indigo-400 mb-2" />
              <p className="text-[13px] font-semibold">Kanban &amp; List</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Drag, drop, done.</p>
            </div>
            <div className="p-3.5 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
              <ChartBar weight="fill" className="w-5 h-5 text-purple-400 mb-2" />
              <p className="text-[13px] font-semibold">Live Insights</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Burndown &amp; velocity.</p>
            </div>
            <div className="p-3.5 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
              <UsersThree weight="fill" className="w-5 h-5 text-pink-400 mb-2" />
              <p className="text-[13px] font-semibold">Team Sync</p>
              <p className="text-[11px] text-slate-400 mt-0.5">@mentions &amp; email.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-10 lg:hidden flex items-center gap-2.5">
            <BrandLogo size="lg" />
            <h1 className="font-['Outfit'] text-2xl font-bold text-zinc-900 tracking-tight">Inspiro Projects</h1>
          </div>
          <div className="hidden lg:block mb-6">
            <h2 className="font-['Outfit'] text-3xl font-semibold text-zinc-900 tracking-tight">{tab === 'login' ? 'Welcome back' : 'Get started free'}</h2>
            <p className="text-sm text-zinc-500 mt-1">{tab === 'login' ? 'Sign in to your workspace' : 'Create your workspace in seconds — no credit card required.'}</p>
          </div>

          <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(''); }} className="w-full">
            {invitationInfo && (
              <div className="mb-5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3" data-testid="invitation-banner">
                <p className="text-[13px] text-blue-900">
                  <strong>You're invited</strong> to join <strong>{invitationInfo.org_name}</strong> as <strong>{invitationInfo.role}</strong>.
                </p>
                <p className="text-[11px] text-blue-700 mt-1">Create your account to accept this invitation.</p>
              </div>
            )}
            {pendingVerification && (
              <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4" data-testid="pending-verification-banner">
                <p className="text-[14px] font-semibold text-emerald-900">Check your email</p>
                <p className="text-[12px] text-emerald-700 mt-1">We sent a verification link to <strong>{pendingVerification.email}</strong>. Click the link in your email to activate your account.</p>
                <div className="mt-2 flex items-center gap-2">
                  <button type="button" onClick={() => handleResendVerification(pendingVerification.email)} className="text-[11px] font-medium text-emerald-700 underline hover:text-emerald-900" data-testid="resend-verification-pending">
                    {resendStatus === 'sending' ? 'Sending…' : resendStatus === 'sent' ? 'Sent!' : 'Resend link'}
                  </button>
                  <span className="text-zinc-300">·</span>
                  <button type="button" onClick={() => { setPendingVerification(null); setTab('login'); }} className="text-[11px] font-medium text-zinc-600 underline hover:text-zinc-800">Back to login</button>
                </div>
              </div>
            )}
            {needsVerification && (
              <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-4" data-testid="needs-verification-banner">
                <p className="text-[14px] font-semibold text-amber-900">Email not verified</p>
                <p className="text-[12px] text-amber-700 mt-1">Your email <strong>{needsVerification.email}</strong> is not verified yet. Please check your inbox for the verification link.</p>
                <div className="mt-2 flex items-center gap-2">
                  <button type="button" onClick={() => handleResendVerification(needsVerification.email)} className="text-[11px] font-medium text-amber-700 underline hover:text-amber-900" data-testid="resend-verification-login">
                    {resendStatus === 'sending' ? 'Sending…' : resendStatus === 'sent' ? 'Sent!' : 'Resend verification email'}
                  </button>
                  <span className="text-zinc-300">·</span>
                  <button type="button" onClick={() => setNeedsVerification(null)} className="text-[11px] font-medium text-zinc-600 underline hover:text-zinc-800">Dismiss</button>
                </div>
              </div>
            )}
            <TabsList className="grid grid-cols-2 mb-8 bg-zinc-100">
              <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium">Email</Label>
                  <div className="relative">
                    <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                    <Input id="login-email" data-testid="login-email-input" type="email" placeholder="you@company.com" className="pl-10 h-11 border-zinc-200" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} required />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password" className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium">Password</Label>
                    <Link to="/forgot-password" className="text-[11px] text-indigo-600 hover:text-indigo-700 font-medium" data-testid="forgot-password-link">Forgot password?</Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                    <Input id="login-password" data-testid="login-password-input" type={showLoginPassword ? 'text' : 'password'} placeholder="Enter password" className="pl-10 pr-10 h-11 border-zinc-200" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} required />
                    <button type="button" onClick={() => setShowLoginPassword(!showLoginPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" tabIndex={-1} data-testid="toggle-login-password">
                      {showLoginPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
                <Button type="submit" data-testid="login-submit-button" disabled={submitting} className="w-full h-11 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium rounded-md transition-all duration-200 shadow-md shadow-indigo-500/20">
                  {submitting ? 'Signing in...' : 'Sign In'}
                  {!submitting && <ArrowRight className="ml-2" size={16} />}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="reg-name" className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                    <Input id="reg-name" data-testid="register-name-input" type="text" placeholder="Jane Smith" className="pl-10 h-11 border-zinc-200" value={regName} onChange={e => setRegName(e.target.value)} required />
                  </div>
                </div>
                {!invitationToken && (
                  <div className="space-y-2">
                    <Label htmlFor="reg-org" className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium">Company / Organization</Label>
                    <div className="relative">
                      <Buildings className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                      <Input id="reg-org" data-testid="register-org-input" type="text" placeholder="Acme Inc." className="pl-10 h-11 border-zinc-200" value={regOrgName} onChange={e => setRegOrgName(e.target.value)} required />
                    </div>
                    <p className="text-[11px] text-zinc-400">Your workspace will be created under this name. One workspace per company.</p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="reg-email" className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium">Email</Label>
                  <div className="relative">
                    <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                    <Input id="reg-email" data-testid="register-email-input" type="email" placeholder="you@company.com" className="pl-10 h-11 border-zinc-200" value={regEmail} onChange={e => setRegEmail(e.target.value)} required />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password" className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                    <Input id="reg-password" data-testid="register-password-input" type={showRegPassword ? 'text' : 'password'} placeholder="Min 6 characters" className="pl-10 pr-10 h-11 border-zinc-200" value={regPassword} onChange={e => setRegPassword(e.target.value)} required minLength={6} />
                    <button type="button" onClick={() => setShowRegPassword(!showRegPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" tabIndex={-1} data-testid="toggle-register-password">
                      {showRegPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
                <Button type="submit" data-testid="register-submit-button" disabled={submitting} className="w-full h-11 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium rounded-md transition-all duration-200 shadow-md shadow-indigo-500/20">
                  {submitting ? 'Creating account...' : 'Create Account'}
                  {!submitting && <ArrowRight className="ml-2" size={16} />}
                </Button>
                <p className="text-[11px] text-zinc-400 text-center pt-1">By signing up you agree to our Terms of Service and Privacy Policy.</p>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-200" /></div>
              <div className="relative flex justify-center text-xs uppercase tracking-[0.15em]">
                <span className="bg-white px-3 text-zinc-400">or continue with</span>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Button data-testid="google-login-button" variant="outline" onClick={handleGoogleLogin} className="h-11 border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300 transition-colors duration-200 text-zinc-700 font-medium shadow-sm">
                <svg className="w-[18px] h-[18px] mr-2" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google
              </Button>
              <Button data-testid="microsoft-login-button" variant="outline" onClick={handleMicrosoftLogin} className="h-11 border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300 transition-colors duration-200 text-zinc-700 font-medium shadow-sm">
                <svg className="w-[16px] h-[16px] mr-2" viewBox="0 0 23 23" aria-hidden="true">
                  <path fill="#F35325" d="M1 1h10v10H1z"/>
                  <path fill="#81BC06" d="M12 1h10v10H12z"/>
                  <path fill="#05A6F0" d="M1 12h10v10H1z"/>
                  <path fill="#FFBA08" d="M12 12h10v10H12z"/>
                </svg>
                Microsoft
              </Button>
            </div>
            <p className="mt-3 text-center text-[11px] text-zinc-400">
              One-click sign-in. New users will be prompted to name their workspace.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
